import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const PageDevis = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const adresseDepartFromHome = location.state?.adresseDepart || "";

  const departRef = useRef(null);
  const arriveeRef = useRef(null);

  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    dateDepart: "",
    email: "",
    telephone: "",
  });

  const [logementDepart, setLogementDepart] = useState("");
  const [etageDepart, setEtageDepart] = useState("");
  const [ascenseurDepart, setAscenseurDepart] = useState("");

  const [logementArrivee, setLogementArrivee] = useState("");
  const [etageArrivee, setEtageArrivee] = useState("");
  const [ascenseurArrivee, setAscenseurArrivee] = useState("");

  const [distanceInfo, setDistanceInfo] = useState(null);

  const [dateError, setDateError] = useState(false);
  const [adresseDepartError, setAdresseDepartError] = useState(false);
  const [adresseArriveeError, setAdresseArriveeError] = useState(false);
  const [telephoneError, setTelephoneError] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const [departPlaceOk, setDepartPlaceOk] = useState(false);
  const [arriveePlaceOk, setArriveePlaceOk] = useState(false);

  const getAdresseDepart = () => departRef.current?.value || "";
  const getAdresseArrivee = () => arriveeRef.current?.value || "";

  const { minDateISO, maxDateISO } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const min = new Date(today.getTime() - today.getTimezoneOffset() * 60000);

    const max = new Date(today);
    max.setFullYear(max.getFullYear() + 2);
    max.setHours(0, 0, 0, 0);
    const maxLocal = new Date(max.getTime() - max.getTimezoneOffset() * 60000);

    return {
      minDateISO: min.toISOString().slice(0, 10),
      maxDateISO: maxLocal.toISOString().slice(0, 10),
    };
  }, []);

  const formatDateFR = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  const isValidAddressText = (raw) => {
    const s = (raw || "").trim();
    if (!s) return false;
    if (/^\d{5}$/.test(s)) return false;
    if (!/[A-Za-zÀ-ÖØ-öø-ÿ]/.test(s)) return false;
    if (!/\d/.test(s)) return false;
    if (s.length < 10) return false;
    const words = s.split(/\s+/).filter(Boolean);
    if (words.length < 3) return false;
    return true;
  };

  const isDateValide = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return false;
    return dateStr >= minDateISO && dateStr <= maxDateISO;
  };

  const sanitizePhone = (v) => (v || "").replace(/\D/g, "");
  const isValidPhoneFR = (digits) => {
    if (!digits) return false;
    if (/^0\d{9}$/.test(digits)) return true;
    if (/^33\d{9}$/.test(digits)) return true;
    return false;
  };

  const isValidEmailSimple = (email) => {
    const e = (email || "").trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+[A-Za-z]$/;
    return emailRegex.test(e);
  };

  const calculerDistance = () => {
    const adresseDepart = getAdresseDepart();
    const adresseArrivee = getAdresseArrivee();

    if (!adresseDepart || !adresseArrivee) {
      setDistanceInfo(null);
      return;
    }

    if (!window.google || !window.google.maps) {
      console.error("Google Maps n'est pas chargé");
      return;
    }

    const service = new window.google.maps.DistanceMatrixService();

    service.getDistanceMatrix(
      {
        origins: [adresseDepart],
        destinations: [adresseArrivee],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status !== "OK") {
          console.error("DistanceMatrix error:", status);
          setDistanceInfo(null);
          return;
        }

        const result = response.rows[0]?.elements[0];
        if (result && result.status === "OK") {
          setDistanceInfo({
            distanceText: result.distance.text,
            distanceValue: result.distance.value,
            durationText: result.duration.text,
          });
        } else {
          setDistanceInfo(null);
        }
      }
    );
  };

  useEffect(() => {
    if (adresseDepartFromHome && departRef.current) {
      departRef.current.value = adresseDepartFromHome;
      setDepartPlaceOk(true);
      setAdresseDepartError(false);
    }
  }, [adresseDepartFromHome]);

  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) return;

    const options = { types: ["address"], componentRestrictions: { country: "fr" } };

    if (departRef.current) {
      const a = new window.google.maps.places.Autocomplete(departRef.current, options);
      a.addListener("place_changed", () => {
        const place = a.getPlace();
        const ok = !!place?.address_components?.length;

        setDepartPlaceOk(ok);
        setAdresseDepartError(!ok);

        if (ok && place?.formatted_address && departRef.current) {
          departRef.current.value = place.formatted_address;
        }

        calculerDistance();
      });
    }

    if (arriveeRef.current) {
      const a = new window.google.maps.places.Autocomplete(arriveeRef.current, options);
      a.addListener("place_changed", () => {
        const place = a.getPlace();
        const ok = !!place?.address_components?.length;

        setArriveePlaceOk(ok);
        setAdresseArriveeError(!ok);

        if (ok && place?.formatted_address && arriveeRef.current) {
          arriveeRef.current.value = place.formatted_address;
        }

        calculerDistance();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (logementDepart !== "appartement") {
      setEtageDepart("");
      setAscenseurDepart("");
    }
  }, [logementDepart]);

  useEffect(() => {
    if (logementArrivee !== "appartement") {
      setEtageArrivee("");
      setAscenseurArrivee("");
    }
  }, [logementArrivee]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const digits = sanitizePhone(e.target.value);
    setFormData((p) => ({ ...p, telephone: digits }));
    setTelephoneError(false);
  };

  const handleEmailChange = (e) => {
    setFormData((p) => ({ ...p, email: e.target.value }));
    setEmailError(false);
  };

  const logementDepartOK =
    logementDepart === "maison" ||
    (logementDepart === "appartement" &&
      (ascenseurDepart === "oui" || ascenseurDepart === "non") &&
      etageDepart !== "" &&
      Number(etageDepart) >= 0);

  const logementArriveeOK =
    logementArrivee === "maison" ||
    (logementArrivee === "appartement" &&
      (ascenseurArrivee === "oui" || ascenseurArrivee === "non") &&
      etageArrivee !== "" &&
      Number(etageArrivee) >= 0);

  const adresseDepartOK = departPlaceOk && isValidAddressText(getAdresseDepart());
  const adresseArriveeOK = arriveePlaceOk && isValidAddressText(getAdresseArrivee());

  const telOK = isValidPhoneFR(formData.telephone);
  const emailOK = isValidEmailSimple(formData.email);
  const dateOK = isDateValide(formData.dateDepart);

  const formValid =
    formData.nom.trim() &&
    formData.prenom.trim() &&
    adresseDepartOK &&
    adresseArriveeOK &&
    formData.dateDepart.trim() &&
    emailOK &&
    telOK &&
    logementDepartOK &&
    logementArriveeOK;

  const handleTarifsClick = (e) => {
    e.preventDefault();

    if (!formData.dateDepart.trim() || !dateOK) {
      setDateError(true);
      alert(
        `Date autorisée : du ${formatDateFR(minDateISO)} au ${formatDateFR(maxDateISO)}`
      );
      return;
    }

    const errors = [];

    if (!formData.nom.trim()) errors.push("Nom");
    if (!formData.prenom.trim()) errors.push("Prénom");

    if (!adresseDepartOK) {
      setAdresseDepartError(true);
      errors.push("Adresse de départ (sélection Google obligatoire)");
    }
    if (!adresseArriveeOK) {
      setAdresseArriveeError(true);
      errors.push("Adresse d'arrivée (sélection Google obligatoire)");
    }

    if (!emailOK) {
      setEmailError(true);
      errors.push("Email (doit contenir @ + un point + finir par une lettre)");
    }

    if (!telOK) {
      setTelephoneError(true);
      errors.push("Téléphone (chiffres uniquement)");
    }

    if (!logementDepart) errors.push("Type de logement (départ)");
    if (logementDepart === "appartement") {
      if (!(ascenseurDepart === "oui" || ascenseurDepart === "non")) {
        errors.push("Ascenseur (départ)");
      }
      if (etageDepart === "" || Number(etageDepart) < 0) {
        errors.push("Étage (départ)");
      }
    }

    if (!logementArrivee) errors.push("Type de logement (arrivée)");
    if (logementArrivee === "appartement") {
      if (!(ascenseurArrivee === "oui" || ascenseurArrivee === "non")) {
        errors.push("Ascenseur (arrivée)");
      }
      if (etageArrivee === "" || Number(etageArrivee) < 0) {
        errors.push("Étage (arrivée)");
      }
    }

    if (errors.length) {
      alert("Veuillez remplir correctement :\n- " + errors.join("\n- "));
      return;
    }

    setDateError(false);
    setAdresseDepartError(false);
    setAdresseArriveeError(false);
    setTelephoneError(false);
    setEmailError(false);

    const adresseDepart = getAdresseDepart();
    const adresseArrivee = getAdresseArrivee();

    const userInfo = {
      ...formData,
      adresseDepart,
      adresseArrivee,
      distance: distanceInfo?.distanceText || "",
      dureeTrajet: distanceInfo?.durationText || "",
      logementDepart,
      ascenseurDepart,
      etageDepart,
      logementArrivee,
      ascenseurArrivee,
      etageArrivee,
    };

    navigate("/tarifs", { state: { userInfo } });
  };

  return (
    <div className="min-h-screen bg-gray-100 overflow-x-hidden">
      {/* NAVBAR */}
      <nav className="bg-blue-600 text-white px-4 sm:px-6 py-4 relative z-50">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
          <div className="flex justify-center lg:justify-start items-center">
            <img
              src="/Logo.svg"
              alt="Logo"
              className="h-14 sm:h-20 lg:h-24 w-auto max-w-[220px] sm:max-w-[300px] lg:max-w-[380px] object-contain block"
            />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 lg:gap-6">
            <button
              className="w-full sm:w-auto px-4 py-2 bg-blue-700/60 rounded-full text-base sm:text-lg lg:text-[20px] hover:bg-white/30 transition"
              onClick={() => navigate("/")}
            >
              Accueil
            </button>

            <button
              className="w-full sm:w-auto px-4 py-2 bg-blue-700/60 rounded-full text-base sm:text-lg lg:text-[20px] hover:bg-white/30 transition"
              onClick={() => navigate("/devis")}
            >
              Devis
            </button>

            <button className="w-full sm:w-auto px-4 py-2 bg-blue-700/60 rounded-full text-base sm:text-lg lg:text-[20px] hover:bg-white/30 transition-colors">
              Avis
            </button>
          </div>

          <div className="flex items-center justify-center lg:justify-end">
            <div className="flex flex-col items-center">
              <button className="bg-red-500 px-4 py-2 rounded-md font-bold text-white text-sm sm:text-base">
                07 66 11 91 93
              </button>
              <span className="mt-1 text-xs sm:text-sm">Assistant client</span>
            </div>
          </div>
        </div>
      </nav>

      {/* FORM */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h2 className="text-xl sm:text-2xl font-bold text-center mb-6">
          Demande de Devis
        </h2>

        <div className="bg-white shadow-lg rounded-lg p-4 sm:p-6 lg:p-8 border border-gray-200">
          <form className="space-y-5">
            <div>
              <label className="block text-sm font-semibold mb-1">Nom</label>
              <input
                type="text"
                name="nom"
                value={formData.nom}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Prénom</label>
              <input
                type="text"
                name="prenom"
                value={formData.prenom}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base"
                required
              />
            </div>

            <div className="rounded-lg border border-gray-200 p-4 sm:p-5">
              <label className="block text-sm font-semibold mb-1">
                Adresse de départ
              </label>
              <input
                ref={departRef}
                type="text"
                className={`w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base ${
                  adresseDepartError ? "border-red-500" : ""
                }`}
                placeholder="Commencez à taper votre adresse..."
                onChange={() => {
                  setAdresseDepartError(false);
                  setDepartPlaceOk(false);
                }}
                onBlur={() => {
                  const ok = departPlaceOk && isValidAddressText(getAdresseDepart());
                  setAdresseDepartError(!ok);
                }}
                required
              />
              {adresseDepartError && (
                <p className="text-sm text-red-600 mt-1">
                  Sélectionnez une adresse valide.
                </p>
              )}

              <div className="mt-4">
                <label className="block text-sm font-semibold mb-1">
                  Type de logement (départ)
                </label>
                <select
                  value={logementDepart}
                  onChange={(e) => setLogementDepart(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base"
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="maison">Maison</option>
                  <option value="appartement">Appartement</option>
                </select>
              </div>

              {logementDepart === "appartement" && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      Ascenseur (départ) ?
                    </label>
                    <select
                      value={ascenseurDepart}
                      onChange={(e) => setAscenseurDepart(e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base"
                      required
                    >
                      <option value="">-- Sélectionner --</option>
                      <option value="oui">Oui</option>
                      <option value="non">Non</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      Étage (départ)
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Ex: 3"
                      value={etageDepart}
                      onChange={(e) => setEtageDepart(e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base"
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 p-4 sm:p-5">
              <label className="block text-sm font-semibold mb-1">
                Adresse d'arrivée
              </label>
              <input
                ref={arriveeRef}
                type="text"
                className={`w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base ${
                  adresseArriveeError ? "border-red-500" : ""
                }`}
                placeholder="Commencez à taper votre adresse..."
                onChange={() => {
                  setAdresseArriveeError(false);
                  setArriveePlaceOk(false);
                }}
                onBlur={() => {
                  const ok = arriveePlaceOk && isValidAddressText(getAdresseArrivee());
                  setAdresseArriveeError(!ok);
                }}
                required
              />
              {adresseArriveeError && (
                <p className="text-sm text-red-600 mt-1">
                  Sélectionnez une adresse valide.
                </p>
              )}

              <div className="mt-4">
                <label className="block text-sm font-semibold mb-1">
                  Type de logement (arrivée)
                </label>
                <select
                  value={logementArrivee}
                  onChange={(e) => setLogementArrivee(e.target.value)}
                  className="w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base"
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="maison">Maison</option>
                  <option value="appartement">Appartement</option>
                </select>
              </div>

              {logementArrivee === "appartement" && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      Ascenseur (arrivée) ?
                    </label>
                    <select
                      value={ascenseurArrivee}
                      onChange={(e) => setAscenseurArrivee(e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base"
                      required
                    >
                      <option value="">-- Sélectionner --</option>
                      <option value="oui">Oui</option>
                      <option value="non">Non</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      Étage (arrivée)
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Ex: 2"
                      value={etageArrivee}
                      onChange={(e) => setEtageArrivee(e.target.value)}
                      className="w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base"
                      required
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                Date de départ
              </label>
              <input
                type="date"
                name="dateDepart"
                value={formData.dateDepart}
                min={minDateISO}
                max={maxDateISO}
                onChange={(e) => {
                  handleChange(e);
                  setDateError(false);
                }}
                onBlur={() => {
                  if (formData.dateDepart.trim() && !isDateValide(formData.dateDepart)) {
                    setDateError(true);
                  }
                }}
                className={`w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base ${
                  dateError ? "border-red-500" : ""
                }`}
                required
              />

              {dateError && (
                <p className="text-sm text-red-600 mt-1">
                  Date autorisée : du {formatDateFR(minDateISO)} au{" "}
                  {formatDateFR(maxDateISO)}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleEmailChange}
                onBlur={() => setEmailError(!isValidEmailSimple(formData.email))}
                className={`w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base ${
                  emailError ? "border-red-500" : ""
                }`}
                placeholder="ex: monmail@gmail.com"
                required
              />
              {emailError && (
                <p className="text-sm text-red-600 mt-1">
                  Veuillez entrer un email valide.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold mb-1">
                Numéro de téléphone
              </label>
              <input
                type="tel"
                name="telephone"
                inputMode="numeric"
                pattern="[0-9]*"
                value={formData.telephone}
                onChange={handlePhoneChange}
                onBlur={() => setTelephoneError(!isValidPhoneFR(formData.telephone))}
                className={`w-full px-4 py-2.5 border rounded-md shadow-sm text-sm sm:text-base ${
                  telephoneError ? "border-red-500" : ""
                }`}
                placeholder="06XXXXXXXX ou 33XXXXXXXXX"
                required
              />
              {telephoneError && (
                <p className="text-sm text-red-600 mt-1">
                  Téléphone invalide (chiffres uniquement). Ex: 06XXXXXXXX ou
                  33XXXXXXXXX.
                </p>
              )}
            </div>

            {distanceInfo && (
              <div className="mt-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-md text-sm">
                <p>
                  <span className="font-semibold">Distance estimée :</span>{" "}
                  {distanceInfo.distanceText}
                </p>
                <p>
                  <span className="font-semibold">Durée de trajet estimée :</span>{" "}
                  {distanceInfo.durationText}
                </p>
              </div>
            )}

            <div className="text-center mt-6">
              <button
                onClick={handleTarifsClick}
                className={`w-full sm:w-auto px-6 py-3 rounded-md shadow-md text-white text-sm sm:text-base ${
                  formValid ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"
                }`}
                disabled={!formValid}
              >
                Continuer
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PageDevis;